import React from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { Header, TextInput, Button, Tooltip, Select } from 'components/common'
import { OnboardFormStateType, CustomerRegion } from '../types'

interface Step2Props {
  state: OnboardFormStateType
  localDispatch: React.Dispatch<any>
  customerRegions: CustomerRegion[]
  agree: boolean
  setAgree: (value: boolean) => void
  onNext: () => void
  canProceed: boolean
  submitting: boolean
}

export const Step2 = ({
  state,
  localDispatch,
  customerRegions,
  agree,
  setAgree,
  onNext,
  canProceed,
  submitting,
}: Step2Props) => {
  const { t } = useTranslation('pub', {
    keyPrefix: 'PublicRoutes.signUp',
  })

  return (
    <div className="form-step">
      <Header as="h3">{t('accountDetails', 'Account Details')}</Header>
      <div className="onboard-message">{t('allRequired')}</div>

      <TextInput
        id="first-name"
        label={t('firstName')}
        placeholder="First"
        value={state.firstName}
        onChange={({
          target: { value },
        }: React.ChangeEvent<HTMLInputElement>) =>
          localDispatch({
            type: 'SET_INPUT',
            value,
            input: 'firstName',
          })
        }
        fullBorder
        error={state.errors.firstName}
      />
      <TextInput
        id="last-name"
        label={t('lastName')}
        placeholder="Last"
        value={state.lastName}
        onChange={({
          target: { value },
        }: React.ChangeEvent<HTMLInputElement>) =>
          localDispatch({
            type: 'SET_INPUT',
            value,
            input: 'lastName',
          })
        }
        fullBorder
        error={state.errors.lastName}
      />
      <TextInput
        id="email"
        label={t('email')}
        placeholder="example@email.com"
        value={state.email}
        onChange={({
          target: { value },
        }: React.ChangeEvent<HTMLInputElement>) =>
          localDispatch({ type: 'SET_INPUT', value, input: 'email' })
        }
        fullBorder
        error={state.errors.email}
      />
      <TextInput
        id="password"
        type="password"
        label={t('password')}
        placeholder="**********"
        autoComplete="new-password"
        value={state.password}
        onChange={({
          target: { value },
        }: React.ChangeEvent<HTMLInputElement>) =>
          localDispatch({
            type: 'SET_INPUT',
            value,
            input: 'password',
          })
        }
        fullBorder
        error={state.errors.password}
      />
      <TextInput
        id="passwordConfirm"
        type="password"
        label={t('passwordConfirm')}
        placeholder="**********"
        autoComplete="new-password"
        value={state.passwordConfirm}
        onChange={({
          target: { value },
        }: React.ChangeEvent<HTMLInputElement>) =>
          localDispatch({
            type: 'SET_INPUT',
            value,
            input: 'passwordConfirm',
          })
        }
        fullBorder
        error={state.errors.passwordConfirm}
      />
      {/* 
// @ts-ignore */}
      <Select
        id="customerRegion"
        label={t('country')}
        isSearchable={true}
        selected={
          state.customerRegion != null
            ? {
                value: state.customerRegion,
                label:
                  customerRegions.find((cr) => cr.id === state.customerRegion)
                    ?.country || '',
              }
            : null
        }
        options={customerRegions.map((cr) => ({
          value: cr.id,
          label: cr.country,
        }))}
        onChange={({ value }: { label: string; value: number }) =>
          localDispatch({
            type: 'SET_INPUT',
            value,
            input: 'customerRegion',
          })
        }
        error={state.errors.customerRegion}
      />

      <div className="agreement-container">
        <input
          type="checkbox"
          id="agreement"
          name="agreement"
          onChange={() => setAgree(!agree)}
          checked={agree}
        />
        <label htmlFor="agreement" className="agreement-message">
          <Trans t={t} i18nKey="confirmTerms">
            I confirm that I have read and agree to the My Cyber Risk&nbsp;
            <a href={t('termsConditionsLink')} target="_blank" rel="noreferrer">
              Terms & Conditions
            </a>
            &nbsp;and&nbsp;
            <a href={t('privacyNoticeLink')} target="_blank" rel="noreferrer">
              Privacy Notice
            </a>
            .
          </Trans>
        </label>
      </div>

      <div className="step-navigation">
        <Tooltip
          title={t('termsTooltip')}
          disabled={canProceed}
          style={{ display: 'flex' }}
        >
          <Button
            color="blue"
            onClick={onNext}
            disabled={!canProceed || submitting}
            loading={submitting}
          >
            {t('createAccount', 'Create Account')}
          </Button>
        </Tooltip>
      </div>
    </div>
  )
}
